import { Component, OnInit } from '@angular/core';
import { MenuItem, ConfirmationService } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TechnologyComponent } from './technology/technology.component';
import { SkillCategoryComponent } from './skill-category/skill-category.component';
import { CommonModule } from '@angular/common';
import { SectorComponent } from './sector/sector.component';
import { NearshoreComponent } from './nearshore/nearshore.component';
import { OffshoreLevelComponent } from './offshore-level/offshore-level.component';
import { ClusterComponent } from './cluster/cluster.component';
import { CategoryComponent } from './category/category.component';
import { PoolComponent } from './pool/pool.component';
import { CareerPathComponent } from './career-path/career-path.component';
import { PracticesComponent } from './practices/practices.component';
import { CountryComponent } from './country/country.component';
import { RegionComponent } from './region/region.component';
import { StatusComponent } from './status/status.component';
import { RuntimeSettingsComponent } from './runtime-settings/runtime-settings.component';
import { DocumentTemplateComponent } from './document-template/document-template.component';
import { StaffedRoleComponent } from './staffed-role/staffed-role.component';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyExchangeComponent } from './currency-exchange/currency-exchange.component';
import { EvaluationComponent } from './evaluation/evaluation.component';
import { RoleMgmtComponent } from './role-mgmt/role-mgmt.component';
import { RoleUsersComponent } from './role-users/role-users.component';
import { RolesComponent } from './roles/roles.component';
import { Permission } from '../../core/enums/permission.enum';
import { PermissionService } from '../../core/services/permission.service';
import { EmPermissionDirective } from '../../shared/directives/has-permission.directive';
import { WorkingHoursComponent } from './working-hours/working-hours.component';
import { GuardConfigurationComponent } from './guard-configuration/guard-configuration.component';
import { JiraPkeyComponent } from './jira-project-key/jira-project-key.component';
import { InvoicingProjectAccountComponent } from './invoicing-project-account/invoicing-project-account.component';
import { CurrencyComponent } from './currency/currency.component';
import { InvoicingSettingsComponent } from './invoicing-settings/invoicing-settings.component';
import { TrainingBatchSettingsComponent } from './training-batch-settings/training-batch-settings.component';
import { OpportunityReferenceDataComponent } from './opportunity-reference-data/opportunity-reference-data.component';
import { UnsavedChangesService } from '../../core/services/unsaved-changes.service';
import { environment } from '../../../environments/environment';
import { hasMenuPermission, MenuPermission } from './menu-permission';
import { PracticeScopeService } from '../../core/services/practice-scope.service';

/** Who may open Configuration > Evaluation (Category tab: CONFIG_PERFORMANCE; Remuneration tab: Manager/Director). */
const EVALUATION_CONFIG_PERMISSIONS = [
  Permission.CONFIG_PERFORMANCE,
  Permission.MANAGER_READ,
  Permission.MANAGER_WRITE,
  Permission.DIRECTOR_READ,
  Permission.DIRECTOR_WRITE,
];

@Component({
  selector: 'em-configurations',
  standalone: true,
  imports: [
    CommonModule,
    MenuModule,
    ConfirmDialogModule,
    TechnologyComponent,
    SkillCategoryComponent,
    SectorComponent,
    NearshoreComponent,
    OffshoreLevelComponent,
    ClusterComponent,
    CategoryComponent,
    CareerPathComponent,
    PracticesComponent,
    CurrencyComponent,
    CurrencyExchangeComponent,
    CountryComponent,
    RegionComponent,
    StatusComponent,
    WorkingHoursComponent,
    GuardConfigurationComponent,
    RuntimeSettingsComponent,
    DocumentTemplateComponent,
    StaffedRoleComponent,
    EvaluationComponent,
    PoolComponent,
    RoleMgmtComponent,
    RoleUsersComponent,
    RolesComponent,
    EmPermissionDirective,
    JiraPkeyComponent,
    InvoicingProjectAccountComponent,
    InvoicingSettingsComponent,
    TrainingBatchSettingsComponent,
    OpportunityReferenceDataComponent,
  ],
  templateUrl: './configurations.component.html',
  providers: [ConfirmationService],
})
export class ConfigurationsComponent implements OnInit {
  items: MenuItem[] = [];
  activeItem = '';
  selectedRole: any = null;

  public Permission = Permission;

  /**
   * The Pool holds the categories a manager may pick from. Beside the
   * configuration permission it is open to the managers who are confined to their
   * own practice - they only ever see that practice's categories.
   */
  public get canManagePool(): boolean {
    return this.permissionService.hasPermission(Permission.CONFIG_PERFORMANCE)
      || this.practiceScope.isPracticeScopedUser();
  }

  /**
   * The Evaluation configuration holds the categories, the remuneration grid and
   * the salary ranges. It is open to the managers who are confined to their own
   * practice - they only ever see that practice's data.
   */
  public get canManageEvaluation(): boolean {
    return this.permissionService.hasAnyPermission(EVALUATION_CONFIG_PERMISSIONS)
      || this.practiceScope.isPracticeScopedUser();
  }

  private readonly menuDefinitions: Array<{
    key: string;
    labelKey: string;
    icon: string;
    permission: MenuPermission;
    fallbackLabel?: string;
  }> = [
    { key: 'technologies', labelKey: 'layout.configurations.menu.technologies', icon: 'pi pi-code', permission: Permission.CONFIG_STAFFING },
    { key: 'skill-categories', labelKey: 'layout.configurations.menu.skillCategories', icon: 'pi pi-list', permission: Permission.CONFIG_STAFFING },
    { key: 'sectors', labelKey: 'layout.configurations.menu.sectors', icon: 'pi pi-tags', permission: Permission.CONFIG_STAFFING },
    { key: 'nearshores', labelKey: 'layout.configurations.menu.nearshores', icon: 'pi pi-building', permission: Permission.CONFIG_STAFFING },
    { key: 'clusters', labelKey: 'layout.configurations.menu.clusters', icon: 'pi pi-th-large', permission: Permission.CONFIG_STAFFING },
    { key: 'offshore-levels', labelKey: 'layout.configurations.menu.offshoreLevels', icon: 'pi pi-caret-up', permission: Permission.CONFIG_STAFFING },
    { key: 'practices-page', labelKey: 'layout.configurations.menu.practices', icon: 'pi pi-briefcase', permission: Permission.CONFIG_BSA },
    { key: 'career-paths-page', labelKey: 'layout.configurations.menu.careerPaths', icon: 'pi pi-sitemap', permission: Permission.CONFIG_BSA },
    { key: 'categories', labelKey: 'layout.configurations.menu.categories', icon: 'pi pi-id-card', permission: Permission.CONFIG_BSA },
    { key: 'countries', labelKey: 'layout.configurations.menu.countries', icon: 'pi pi-flag', permission: Permission.CONFIG_BSA },
    { key: 'regions', labelKey: 'layout.configurations.menu.regions', icon: 'pi pi-map', permission: Permission.CONFIG_BSA },
    { key: 'statuses', labelKey: 'layout.configurations.menu.statuses', icon: 'pi pi-circle-on', permission: Permission.CONFIG_BSA },
    { key: 'runtime-settings', labelKey: 'layout.configurations.menu.runtimeSettings', icon: 'pi pi-sliders-v', permission: Permission.CONFIG_BSA },
    { key: 'document-templates', labelKey: 'layout.configurations.menu.documents', icon: 'pi pi-file', permission: Permission.CONFIG_BSA },
    { key: 'staffed-roles', labelKey: 'layout.configurations.menu.staffedRoles', icon: 'pi pi-user', permission: Permission.CONFIG_INVOICING },
    { key: 'working-hours', labelKey: 'layout.configurations.menu.workingHours', icon: 'pi pi-clock', permission: Permission.CONFIG_INVOICING },
    { key: 'guard-configuration', labelKey: 'layout.configurations.menu.guardConfiguration', icon: 'pi pi-calendar', permission: Permission.CONFIG_INVOICING },
    { key: 'jira-project-keys', labelKey: 'layout.configurations.menu.jiraProjectKeys', icon: 'pi pi-key', permission: Permission.CONFIG_INVOICING },
    { key: 'invoicing-project-accounts', labelKey: 'layout.configurations.menu.invoicingProjectAccounts', icon: 'pi pi-briefcase', permission: Permission.CONFIG_INVOICING },
    { key: 'invoicing-settings', labelKey: 'layout.configurations.menu.invoicingSettings', fallbackLabel: 'Invoicing Settings', icon: 'pi pi-sliders-h', permission: Permission.CONFIG_INVOICING },
    { key: 'training-batch', labelKey: 'layout.configurations.menu.trainingBatch', fallbackLabel: 'Training Batch', icon: 'pi pi-file-excel', permission: Permission.CONFIG_INVOICING },
    { key: 'currency', labelKey: 'layout.configurations.menu.currency', icon: 'pi pi-wallet', permission: Permission.CONFIG_INVOICING },
    { key: 'currency-exchange', labelKey: 'layout.configurations.menu.currencyExchange', icon: 'pi pi-arrow-right-arrow-left', permission: Permission.CONFIG_INVOICING },
    {
      key: 'evaluation',
      labelKey: 'layout.configurations.menu.evaluation',
      icon: 'pi pi-book',
      permission: () => this.canManageEvaluation,
    },
    { key: 'pool', labelKey: 'layout.configurations.menu.pool', icon: 'pi pi-users', permission: () => this.canManagePool },
    { key: 'opportunities', labelKey: 'layout.configurations.menu.opportunities', fallbackLabel: 'Opportunities', icon: 'pi pi-briefcase', permission: Permission.CONFIG_PERFORMANCE },
    { key: 'permissions', labelKey: 'layout.configurations.menu.permissions', icon: 'pi pi-shield', permission: Permission.CONFIG_USER_MANAGEMENT },
    { key: 'users', labelKey: 'layout.configurations.menu.users', icon: 'pi pi-user-plus', permission: Permission.CONFIG_USER_MANAGEMENT },
    { key: 'roles', labelKey: 'layout.configurations.menu.roles', icon: 'pi pi-user', permission: Permission.CONFIG_USER_MANAGEMENT },
  ];

  constructor(
    private translateService: TranslateService,
    private permissionService: PermissionService,
    private confirmationService: ConfirmationService,
    private unsavedChangesService: UnsavedChangesService,
    private practiceScope: PracticeScopeService,
  ) {}

  ngOnInit() {
    this.initMenuItems();
    this.translateService.onLangChange.subscribe(() => this.initMenuItems());
  }

  initMenuItems() {
    const allowedMenuDefinitions = this.menuDefinitions.filter((item) => {
      const hasPermission = hasMenuPermission(this.permissionService, item.permission);
      if (!hasPermission) return false;
      const toggleKey = 'show' + item.key.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('');
            if ((environment as any)[toggleKey] === false) {
        return false;
      }

      return true;
    });

    if (!allowedMenuDefinitions.some((item) => item.key === this.activeItem)) {
      this.activeItem = allowedMenuDefinitions[0]?.key ?? '';
    }

    this.translateService.get(allowedMenuDefinitions.map((item) => item.labelKey)).subscribe((translations: any) => {
      this.items = allowedMenuDefinitions.map((item) => ({
        label: (() => {
          const translated = translations?.[item.labelKey];
          if (translated && translated !== item.labelKey) return translated;
          return item.fallbackLabel ?? item.labelKey;
        })(),
        icon: item.icon,
        command: () => this.activeItemChange(item.key),
        styleClass: this.activeItem === item.key ? 'surface-100' : '',
      }));
    });
  }

  async activeItemChange(item: string) {
    try {
      const hasUnsaved = await this.unsavedChangesService.hasUnsavedChanges();
      if (hasUnsaved) {
        this.confirmationService.confirm({
          message: this.translateService.instant('worklog.unsavedChangesMessage'),
          header: this.translateService.instant('worklog.unsavedChangesHeader'),
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: this.translateService.instant('worklog.leave'),
          rejectLabel: this.translateService.instant('worklog.stay'),
          accept: () => {
            this.activeItem = item;
            this.initMenuItems();
          }
        });
      } else {
        this.activeItem = item;
        this.initMenuItems();
      }
    } catch (error) {
      this.activeItem = item;
      this.initMenuItems();
    }
  }

  onRoleSelected(role: any) {
    this.selectedRole = role;
    if (this.permissionService.hasPermission(Permission.CONFIG_USER_MANAGEMENT)) {
      this.activeItemChange('permissions');
    }
  }
}
